import { message } from 'antd'
import { downloadUrl } from 'common/utils/downloadUrl'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import type { UIElementData } from 'photoswipe'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Gallery as PhotoSwipeGallery } from 'react-photoswipe-gallery'
import 'photoswipe/dist/photoswipe.css'

export type GalleryProps = {
  settings?: any
  children: ReactNode
}

/**
 * Same icon, label and toolbar position as the library's `withDownloadButton`,
 * which we can't use: it registers an `<a download target="_blank">` pointing at
 * the slide's S3 URL, and browsers ignore `download` cross-origin, so the click
 * opened the photo in a new tab instead of saving it (CM-4323). A `<button>`
 * that fetches the bytes has no navigation to fall back to.
 */
const downloadButton = (
  onDownload: (src: string) => void,
  ariaLabel: string
): UIElementData => ({
  appendTo: 'bar',
  ariaLabel,
  html: {
    inner:
      '<path d="M20.5 14.3 17.1 18V10h-2.2v7.9l-3.4-3.6L10 16l6 6.1 6-6.1ZM23 23H9v2h14Z" id="pswp__icn-download"/>',
    isCustomSVG: true,
    outlineID: 'pswp__icn-download'
  },
  isButton: true,
  name: 'download-button',
  onClick: (_event, _element, pswp) => {
    const src = pswp.currSlide?.data.src
    if (src) {
      onDownload(src)
    }
  },
  order: 8,
  tagName: 'button'
})

export const Gallery = ({ children, settings }: GalleryProps) => {
  const { t } = useTranslation(NAMESPACE.GENERAL)

  const defaultSettings = {
    gallery: {
      zoom: true,
      initialZoomLevel: 'fit',
      close: true,
      counter: true,
      arrowKeys: true
    },
    ...settings
  }

  const handleDownload = (src: string) => {
    downloadUrl(src).catch(() => {
      message.error(t(generalConstants.COULDNT_DOWNLOAD_FILE))
    })
  }

  return (
    <PhotoSwipeGallery
      options={defaultSettings}
      uiElements={[
        downloadButton(handleDownload, t(generalConstants.DOWNLOAD))
      ]}
    >
      {children}
    </PhotoSwipeGallery>
  )
}
